import { useState, useEffect } from 'react';
import { D, st, toArr, apiGet, apiPost, apiPut, apiDel } from '../constants';
import { Overlay, MHdr, Field, StatGrid } from './shared';

const PAY_TYPES = ["Hourly","Salary","Per Job","Overtime","Bonus","Reimbursement"];
const PAY_STATUS = ["Pending","Paid"];

export default function Payroll({jobs, fmt, fmtDate, setPage}) {
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [tab, setTab] = useState("clock");

  const loadEmployees = () => apiGet('/data/users').then(rows => setEmployees(toArr(rows).filter(u=>u.is_active!==0))).catch(()=>{});
  const loadEntries = () => apiGet('/data/payroll').then(rows => setEntries(toArr(rows))).catch(()=>{});
  useEffect(() => { loadEmployees(); loadEntries(); }, []);

  const [entryModal, setEntryModal] = useState(null);
  const [entryData, setEntryData] = useState({});
  const setEN = (k,v) => setEntryData(x => ({...x, [k]: v}));

  const [empModal, setEmpModal] = useState(false);
  const [empData, setEmpData] = useState({});
  const setEMP = (k,v) => setEmpData(x => ({...x, [k]: v}));

  const [editEmpModal, setEditEmpModal] = useState(false);
  const [editEmpData, setEditEmpData] = useState({});
  const setEED = (k,v) => setEditEmpData(x => ({...x, [k]: v}));

  const [liveSessions, setLiveSessions] = useState([]);
  const [now, setNow] = useState(new Date());

  const loadLiveSessions = async () => {
    try { const data = await apiGet('/employee/clock/all'); setLiveSessions(Array.isArray(data) ? data : []); } catch(_) {}
  };

  useEffect(() => {
    loadLiveSessions();
    const t = setInterval(() => { setNow(new Date()); loadLiveSessions(); }, 30000);
    return () => clearInterval(t);
  }, []);

  const elapsed = (clockedIn) => {
    const ms = now - new Date(clockedIn);
    const h = Math.floor(ms/1000/60/60);
    const m = Math.floor((ms/1000/60)%60);
    return h>0 ? `${h}h ${m}m` : `${m}m`;
  };

  const weekStart = () => { const d=new Date(); d.setDate(d.getDate()-d.getDay()); return d.toISOString().slice(0,10); };
  const ws = weekStart();
  const weekEntries = entries.filter(e => e.date>=ws && (e.pay_type==="Hourly"||e.pay_type==="Overtime"));
  const weekByEmp = {};
  weekEntries.forEach(e => { weekByEmp[e.user_id] = (weekByEmp[e.user_id]||0) + (Number(e.hours)||0); });

  const openAddEntry = () => {
    const first = employees[0];
    setEntryData({userId:first?.id||null, date:new Date().toISOString().slice(0,10), hours:0, payType:first?.pay_type||"Hourly", rate:first?.rate||0, jobId:"", notes:"", status:"Pending"});
    setEntryModal("add");
  };
  const saveEntry = async () => {
    const rec = {...entryData, hours:parseFloat(entryData.hours)||0, rate:parseFloat(entryData.rate)||0};
    const created = await apiPost('/data/payroll', rec);
    setEntries(prev => [...prev, {id:created.id, user_id:rec.userId, date:rec.date, hours:rec.hours, rate:rec.rate, pay_type:rec.payType, job_id:rec.jobId||null, notes:rec.notes, status:rec.status||"Pending"}]);
    setEntryModal(null);
  };
  const deleteEntry = async id => { if (confirm('Delete this entry?')) { await apiDel('/data/payroll/'+id); setEntries(prev => prev.filter(x=>x.id!==id)); } };
  const openAddEmp = () => {
    setEmpData({name:'', role:'crew', payType:'Hourly', rate:'', email:'', phone:''});
    setEmpModal(true);
  };
  const saveEmp = async () => {
    if (!empData.name?.trim()) return;
    try {
      const created = await apiPost('/data/users', empData);
      if (created?.error) { alert(created.error); return; }
      setEmployees(prev => [...prev, {
        id: created.id,
        name: empData.name.trim(),
        email: empData.email || null,
        phone: empData.phone || null,
        role: empData.role || 'crew',
        pay_type: empData.payType || 'Hourly',
        rate: Number(empData.rate) || 0,
        is_active: 1,
        truck_id: null,
      }]);
      setEmpModal(false);
    } catch(err) {
      alert(err?.message || 'Failed to add employee. Check your plan seat limit.');
    }
  };

  const removeEmployee = async (id, name) => {
    if (!confirm(`Remove ${name} from the team? They will be deactivated.`)) return;
    await apiDel('/data/users/' + id);
    setEmployees(prev => prev.filter(e => e.id !== id));
  };

  const openEditEmp = (emp) => {
    setEditEmpData({id:emp.id, name:emp.name, role:emp.role||'crew', payType:emp.pay_type||'Hourly', rate:emp.rate||'', phone:emp.phone||'', email:emp.email||''});
    setEditEmpModal(true);
  };
  const saveEditEmp = async () => {
    if (!editEmpData.name?.trim()) return;
    await apiPut('/data/users/' + editEmpData.id, editEmpData);
    setEmployees(prev => prev.map(e => e.id===editEmpData.id ? {...e, name:editEmpData.name.trim(), role:editEmpData.role, pay_type:editEmpData.payType, rate:Number(editEmpData.rate)||0, phone:editEmpData.phone||null, email:editEmpData.email||null} : e));
    setEditEmpModal(false);
  };

  const sendInvite = async (emp) => {
    try {
      const result = await apiPost('/employee/invite/generate', { userId: emp.id });
      const phone = (emp.phone || '').replace(/\D/g, '');
      const body = encodeURIComponent(`Hi ${emp.name}! You've been added to AmpBooks for time tracking. Set up your PIN here: ${result.url}`);
      window.location.href = `sms:${phone}?body=${body}`;
    } catch(err) {
      alert(err?.message || 'Failed to generate invite link');
    }
  };

  const markPaid = async id => {
    const paidDate = new Date().toISOString().slice(0,10);
    await apiPut('/data/payroll/'+id, {status:"Paid", paidDate});
    setEntries(prev => prev.map(x=>x.id===id?{...x,status:"Paid",paid_date:paidDate}:x));
  };
  const markAllPaid = async ids => {
    const paidDate = new Date().toISOString().slice(0,10);
    await Promise.all(ids.map(id => apiPut('/data/payroll/'+id, {status:"Paid", paidDate})));
    setEntries(prev => prev.map(x=>ids.includes(x.id)?{...x,status:"Paid",paid_date:paidDate}:x));
  };

  const empName = id => employees.find(e=>e.id===id)?.name || "Unknown";
  const roleLabel = r => ({crew:'Foreman',apprentice:'Apprentice',helper:'Apprentice',owner:'Owner'}[r] || r || 'Crew');
  const entryAmount = e => {
    const h = Number(e.hours)||0, r = Number(e.rate)||0;
    const payType = e.pay_type || e.payType;
    if (payType==="Hourly" || payType==="Overtime") return h*r;
    return r;
  };

  const pending = entries.filter(e => e.status==="Pending");
  const paid = entries.filter(e => e.status==="Paid");
  const totalPending = pending.reduce((s,e)=>s+entryAmount(e),0);
  const totalPaid = paid.reduce((s,e)=>s+entryAmount(e),0);

  const pendingByEmp = {};
  pending.forEach(e => { if (!pendingByEmp[e.user_id]) pendingByEmp[e.user_id]=[]; pendingByEmp[e.user_id].push(e); });

  const Tab = ({id,label}) => (
    <button onClick={()=>setTab(id)} style={{padding:"7px 16px",borderRadius:7,fontSize:13,fontWeight:tab===id?600:400,cursor:"pointer",border:"none",background:tab===id?D.accent:"transparent",color:tab===id?"#fff":D.muted}}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div style={st.h1}>Payroll</div>
        <div style={{display:"flex",gap:8}}>
          {tab==="entries" && <button style={st.btn("primary")} onClick={openAddEntry}>+ Log Hours / Pay</button>}
          <button style={st.btn()} onClick={openAddEmp}>+ Add Employee</button>
        </div>
      </div>

      <StatGrid stats={[
        {label:"Pending payroll", value:fmt(totalPending), color:D.warning},
        {label:"Paid this period", value:fmt(totalPaid), color:D.success},
        {label:"Employees", value:employees.length, color:D.accent},
        {label:"Clocked in now", value:liveSessions.filter(s=>s.type==='day').length, color:liveSessions.some(s=>s.type==='day')?D.success:D.muted},
      ]}/>

      <div style={{display:"flex",gap:4,marginBottom:20,background:D.surface,padding:4,borderRadius:10,width:"fit-content",border:`1px solid ${D.border}`,flexWrap:"wrap"}}>
        <Tab id="clock" label="⏱ Time Clock"/>
        <Tab id="entries" label="Pay Entries"/>
        <Tab id="run" label="Payroll Run"/>
      </div>

      {employees.length===0 && (
        <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted,marginBottom:20}}>
          <div style={{fontSize:32,marginBottom:12}}>👷</div>
          <div style={{fontWeight:500,marginBottom:6}}>No team members yet</div>
          <div style={{fontSize:13,marginBottom:16}}>Add employees in Settings → Team before tracking payroll.</div>
          {setPage && <button style={{...st.btn("primary"),marginLeft:0}} onClick={()=>setPage('settings')}>Go to Settings</button>}
        </div>
      )}

      {tab==="clock" && employees.length>0 && <>
        {liveSessions.filter(s=>s.type==='day').length>0 && (
          <div style={{background:D.success+"18",border:`1px solid ${D.success}44`,borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:13,color:D.success,fontWeight:500}}>
            ⏱ {liveSessions.filter(s=>s.type==='day').length} employee{liveSessions.filter(s=>s.type==='day').length>1?"s":""} currently clocked in
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14,marginBottom:20}}>
          {employees.map(emp => {
            const dayS = liveSessions.find(s=>s.user_id===emp.id && s.type==='day');
            const jobS = liveSessions.find(s=>s.user_id===emp.id && s.type==='job');
            const isClockedIn = !!dayS;
            const weekHrs = weekByEmp[emp.id]||0;
            return (
              <div key={emp.id} style={{...st.card,border:`2px solid ${isClockedIn?D.success:D.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  <div style={{width:38,height:38,borderRadius:"50%",background:isClockedIn?D.success:D.surface,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:isClockedIn?"#fff":D.muted,border:`2px solid ${isClockedIn?D.success:D.border}`}}>
                    {(emp.name||"?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:14,color:D.text}}>{emp.name}</div>
                    <div style={{fontSize:11,color:D.muted}}>{roleLabel(emp.role)}{emp.rate!=null?` · $${Number(emp.rate).toFixed(2)}/hr`:""}</div>
                  </div>
                  {isClockedIn && (
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,color:D.success,fontWeight:600}}>● CLOCKED IN</div>
                      <div style={{fontSize:13,fontWeight:700,color:D.success}}>{elapsed(dayS.started_at)}</div>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:10,marginBottom:12}}>
                  <div style={{flex:1,background:D.surface,borderRadius:7,padding:"6px 10px",textAlign:"center"}}>
                    <div style={{fontSize:10,color:D.muted,marginBottom:2}}>This week</div>
                    <div style={{fontSize:16,fontWeight:700,color:weekHrs>=40?D.warning:D.text}}>{weekHrs.toFixed(1)}h</div>
                  </div>
                  <div style={{flex:1,background:D.surface,borderRadius:7,padding:"6px 10px",textAlign:"center"}}>
                    <div style={{fontSize:10,color:D.muted,marginBottom:2}}>Pending pay</div>
                    <div style={{fontSize:16,fontWeight:700,color:D.warning}}>{fmt((pendingByEmp[emp.id]||[]).reduce((s,e)=>s+entryAmount(e),0))}</div>
                  </div>
                </div>
                {isClockedIn ? (<>
                  {jobS ? (
                    <div style={{background:D.success+"18",border:`1px solid ${D.success}44`,borderRadius:8,padding:"8px 12px",marginBottom:8}}>
                      <div style={{fontSize:11,color:D.success,fontWeight:600,marginBottom:2}}>📍 On job</div>
                      <div style={{fontSize:13,fontWeight:600,color:D.text}}>{jobS.job_title}</div>
                      <div style={{fontSize:11,color:D.muted,marginTop:2}}>{elapsed(jobS.started_at)}</div>
                    </div>
                  ) : (
                    <div style={{background:D.surface,borderRadius:8,padding:"8px 12px",marginBottom:8,fontSize:12,color:D.muted,textAlign:"center"}}>
                      Clocked in — no active job
                    </div>
                  )}
                </>) : (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,paddingTop:8,borderTop:`1px solid ${D.border}`}}>
                    <button style={{background:"none",border:"none",color:D.accent,fontSize:11,cursor:"pointer",opacity:0.8}} onClick={()=>openEditEmp(emp)}>✏ Edit</button>
                    <button style={{background:"none",border:"none",color:D.success,fontSize:11,cursor:"pointer",opacity:0.8}} onClick={()=>sendInvite(emp)}>📱 Invite</button>
                    <button style={{background:"none",border:"none",color:D.danger,fontSize:11,cursor:"pointer",opacity:0.7}} onClick={()=>removeEmployee(emp.id,emp.name)}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>}

      {tab==="entries" && (
        entries.length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted}}>
            <div style={{fontSize:32,marginBottom:12}}>💼</div>
            <div style={{fontWeight:500,marginBottom:6}}>No pay entries yet</div>
            <div style={{fontSize:13}}>Log hours or pay using the button above.</div>
          </div>
        ) : (
          <div style={st.card}>
            <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead><tr>{["Date","Employee","Type","Hours","Rate","Amount","Job","Status",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {entries.slice().sort((a,b)=>(b.date||"").localeCompare(a.date||"")).map(e => {
                  const amt = entryAmount(e);
                  const linkedJob = toArr(jobs).find(j=>j.id===e.job_id);
                  return (
                    <tr key={e.id}>
                      <td style={st.td}>{fmtDate(e.date)}</td>
                      <td style={{...st.td,fontWeight:500}}>{empName(e.user_id)}</td>
                      <td style={st.td}><span style={{fontSize:11,background:D.surface,padding:"2px 7px",borderRadius:5,color:D.accent}}>{e.pay_type}</span></td>
                      <td style={st.td}>{e.pay_type==="Hourly"||e.pay_type==="Overtime"?e.hours+"h":"—"}</td>
                      <td style={{...st.td,color:D.muted,fontSize:12}}>{e.rate==null?"—":e.pay_type==="Hourly"||e.pay_type==="Overtime"?"$"+Number(e.rate).toFixed(2)+"/hr":fmt(e.rate)}</td>
                      <td style={{...st.td,color:D.warning,fontWeight:600}}>{fmt(amt)}</td>
                      <td style={{...st.td,fontSize:12,color:D.muted}}>{linkedJob?linkedJob.title:"—"}</td>
                      <td style={st.td}><span style={{background:e.status==="Paid"?"#E1F5EE":"#FAEEDA",color:e.status==="Paid"?D.success:D.warning,fontSize:11,fontWeight:500,padding:"2px 8px",borderRadius:6,border:`0.5px solid ${e.status==="Paid"?D.success:D.warning}`}}>{e.status}</span></td>
                      <td style={st.td}>
                        {e.status==="Pending" && <button style={{...st.btn("success"),padding:"4px 8px",fontSize:11,marginLeft:0}} onClick={()=>markPaid(e.id)}>✓ Pay</button>}
                        <button style={{...st.btn("danger"),padding:"4px 8px",fontSize:11}} onClick={()=>deleteEntry(e.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        )
      )}

      {tab==="run" && <>
        <div style={{background:"#3a0a0a",border:`2px solid ${D.danger}`,borderRadius:10,padding:"14px 18px",marginBottom:16,display:"flex",gap:12,alignItems:"flex-start"}}>
          <span style={{fontSize:24,flexShrink:0}}>⚠️</span>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:D.danger,marginBottom:4}}>Direct Deposit via Stripe is for 1099 Independent Contractors Only</div>
            <div style={{fontSize:12,color:"#ffaaaa",lineHeight:1.7,marginBottom:6}}>
              Stripe Payouts sends payment without any tax withholding. It is <strong style={{color:"#fff"}}>only appropriate for 1099 subcontractors</strong> who are self-employed and handle their own taxes.
            </div>
            <div style={{fontSize:12,color:"#ffaaaa",lineHeight:1.7}}>
              If you have <strong style={{color:"#fff"}}>W-2 employees</strong>, use a payroll provider like <strong style={{color:"#fff"}}>Gusto, Paychex, or ADP</strong> to ensure correct tax withholding, compliance, and year-end W-2 filing. <strong style={{color:"#fff"}}>Misclassification carries serious legal and financial penalties.</strong> Consult a CPA if you are unsure.
            </div>
          </div>
        </div>

        {Object.keys(pendingByEmp).length===0 ? (
          <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted}}>
            <div style={{fontSize:32,marginBottom:12}}>✅</div>
            <div style={{fontWeight:500,marginBottom:6}}>All caught up!</div>
            <div style={{fontSize:13}}>No pending payroll entries.</div>
          </div>
        ) : (
          <>
            {Object.entries(pendingByEmp).map(([empId, empEntries]) => {
              const emp = employees.find(e=>e.id===empId);

              const hourlyEntries = empEntries.filter(e=>e.pay_type==="Hourly");
              const overtimeEntries = empEntries.filter(e=>e.pay_type==="Overtime");
              const bonusEntries = empEntries.filter(e=>e.pay_type==="Bonus");
              const reimbEntries = empEntries.filter(e=>e.pay_type==="Reimbursement");
              const perJobEntries = empEntries.filter(e=>e.pay_type==="Per Job");
              const salaryEntries = empEntries.filter(e=>e.pay_type==="Salary");

              const regularHours = hourlyEntries.reduce((s,e)=>s+(Number(e.hours)||0),0);
              const overtimeHours = overtimeEntries.reduce((s,e)=>s+(Number(e.hours)||0),0);
              const totalHours = regularHours+overtimeHours;

              const autoOT = regularHours>40;
              const autoOTHours = autoOT ? regularHours-40 : 0;
              const autoRegHours = autoOT ? 40 : regularHours;
              const empRate = emp?.rate || hourlyEntries[0]?.rate || 0;
              const otRate = empRate*1.5;

              const regularPay = hourlyEntries.reduce((s,e)=>s+(Number(e.hours)||0)*Number(e.rate||empRate),0);
              const straightTimePay = autoOT ? autoRegHours*empRate : regularPay;
              const autoOTPay = autoOT ? autoOTHours*otRate : 0;
              const overtimePay = overtimeEntries.reduce((s,e)=>s+(Number(e.hours)||0)*Number(e.rate||otRate),0);
              const bonusPay = bonusEntries.reduce((s,e)=>s+(Number(e.rate)||0),0);
              const reimbPay = reimbEntries.reduce((s,e)=>s+(Number(e.rate)||0),0);
              const perJobPay = perJobEntries.reduce((s,e)=>s+(Number(e.rate)||0),0);
              const salaryPay = salaryEntries.reduce((s,e)=>s+(Number(e.rate)||0),0);

              const grossPay = (autoOT?straightTimePay+autoOTPay:regularPay)+overtimePay+bonusPay+perJobPay+salaryPay;
              const totalWithReim = grossPay+reimbPay;

              return (
                <div key={empId} style={{...st.card,border:`1px solid ${D.warning}44`,marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:40,height:40,borderRadius:"50%",background:D.warning+"22",border:`2px solid ${D.warning}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:D.warning,flexShrink:0}}>
                        {(emp?.name||"?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:700,fontSize:15}}>{emp?.name||"Unknown"}</div>
                        <div style={{fontSize:12,color:D.muted}}>{roleLabel(emp?.role)}{empRate>0?" · $"+empRate.toFixed(2)+"/hr":""}</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,color:D.muted,marginBottom:2}}>Gross Pay</div>
                      <div style={{fontSize:24,fontWeight:800,color:D.warning}}>{fmt(grossPay)}</div>
                      {reimbPay>0 && <div style={{fontSize:11,color:D.muted}}>+ {fmt(reimbPay)} reimbursement = {fmt(totalWithReim)} total</div>}
                    </div>
                  </div>

                  <div style={{background:D.surface,border:`1px solid ${D.border}`,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:D.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Pay Calculation</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {(autoOT?autoRegHours:regularHours)>0 && (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
                          <div style={{color:D.muted}}>Regular hours<span style={{color:D.text,fontWeight:500,marginLeft:8}}>{(autoOT?autoRegHours:regularHours).toFixed(2)}h × ${empRate.toFixed(2)}/hr</span></div>
                          <span style={{fontWeight:600,color:D.text}}>{fmt(straightTimePay||regularPay)}</span>
                        </div>
                      )}
                      {autoOT && autoOTHours>0 && (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
                          <div style={{color:D.warning}}>⚠ Overtime detected<span style={{color:D.text,fontWeight:500,marginLeft:8}}>{autoOTHours.toFixed(2)}h × ${otRate.toFixed(2)}/hr (1.5×)</span></div>
                          <span style={{fontWeight:600,color:D.warning}}>{fmt(autoOTPay)}</span>
                        </div>
                      )}
                      {overtimeHours>0 && (
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
                          <div style={{color:D.warning}}>Overtime (logged)<span style={{color:D.text,fontWeight:500,marginLeft:8}}>{overtimeHours.toFixed(2)}h × ${(overtimeEntries[0]?.rate||otRate).toFixed(2)}/hr</span></div>
                          <span style={{fontWeight:600,color:D.warning}}>{fmt(overtimePay)}</span>
                        </div>
                      )}
                      {salaryPay>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><div style={{color:D.muted}}>Salary</div><span style={{fontWeight:600,color:D.text}}>{fmt(salaryPay)}</span></div>}
                      {perJobPay>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><div style={{color:D.muted}}>Per Job</div><span style={{fontWeight:600,color:D.text}}>{fmt(perJobPay)}</span></div>}
                      {bonusPay>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><div style={{color:D.success}}>Bonus</div><span style={{fontWeight:600,color:D.success}}>{fmt(bonusPay)}</span></div>}
                      <div style={{borderTop:`1px solid ${D.border}`,marginTop:4,paddingTop:8,display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:700}}>
                        <span style={{color:D.text}}>GROSS PAY</span><span style={{color:D.warning}}>{fmt(grossPay)}</span>
                      </div>
                      {totalHours>0 && (
                        <div style={{display:"flex",gap:16,marginTop:4,fontSize:11,color:D.muted,flexWrap:"wrap"}}>
                          <span>Regular: <strong style={{color:D.text}}>{(autoOT?autoRegHours:regularHours).toFixed(2)}h</strong></span>
                          {(autoOTHours>0||overtimeHours>0) && <span style={{color:D.warning}}>Overtime: <strong>{(autoOTHours+overtimeHours).toFixed(2)}h</strong></span>}
                          <span>Total: <strong style={{color:D.text}}>{totalHours.toFixed(2)}h</strong></span>
                          {autoOT && <span style={{color:D.warning,fontWeight:600}}>⚠ Exceeds 40h — overtime rate applied</span>}
                        </div>
                      )}
                      {reimbPay>0 && <div style={{borderTop:`1px dashed ${D.border}`,marginTop:6,paddingTop:8,display:"flex",justifyContent:"space-between",fontSize:12}}><span style={{color:D.muted}}>Reimbursements (non-taxable, not in gross)</span><span style={{fontWeight:600,color:D.accent}}>{fmt(reimbPay)}</span></div>}
                      {reimbPay>0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700}}><span style={{color:D.text}}>TOTAL CHECK AMOUNT</span><span style={{color:D.accent}}>{fmt(totalWithReim)}</span></div>}
                    </div>
                  </div>

                  <div style={{overflowX:"auto"}}><table style={{...st.table,fontSize:12}}>
                    <thead><tr>{["Date","Type","Hours","Rate","Amount","Job","Notes"].map(h=><th key={h} style={{...st.th,fontSize:11}}>{h}</th>)}</tr></thead>
                    <tbody>
                      {empEntries.map(e => {
                        const linkedJob = toArr(jobs).find(j=>j.id===e.job_id);
                        return (
                          <tr key={e.id}>
                            <td style={st.td}>{fmtDate(e.date)}</td>
                            <td style={st.td}><span style={{fontSize:10,background:D.surface,padding:"2px 6px",borderRadius:4,color:e.pay_type==="Overtime"?D.warning:e.pay_type==="Bonus"?D.success:D.accent}}>{e.pay_type}</span></td>
                            <td style={st.td}>{e.pay_type==="Hourly"||e.pay_type==="Overtime"?e.hours+"h":"—"}</td>
                            <td style={{...st.td,color:D.muted}}>{e.rate==null?"—":e.pay_type==="Hourly"||e.pay_type==="Overtime"?"$"+Number(e.rate).toFixed(2)+"/hr":fmt(e.rate)}</td>
                            <td style={{...st.td,color:D.warning,fontWeight:600}}>{fmt(entryAmount(e))}</td>
                            <td style={{...st.td,color:D.muted}}>{linkedJob?linkedJob.title:"—"}</td>
                            <td style={{...st.td,color:D.muted,fontSize:11}}>{e.notes||"—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></div>

                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}>
                    <button style={{...st.btn("success"),marginLeft:0,padding:"8px 20px",fontSize:13}} onClick={()=>markAllPaid(empEntries.map(e=>e.id))}>✓ Mark All Paid — {fmt(totalWithReim)}</button>
                  </div>
                </div>
              );
            })}

            <div style={{...st.card,background:D.surface,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontSize:12,color:D.muted,marginBottom:2}}>Total payroll due — {Object.keys(pendingByEmp).length} employee{Object.keys(pendingByEmp).length!==1?"s":""}</div>
                <div style={{fontSize:24,fontWeight:700,color:D.warning}}>{fmt(totalPending)}</div>
              </div>
              <button style={{...st.btn("success"),fontSize:14,padding:"11px 28px",marginLeft:0}} onClick={()=>markAllPaid(pending.map(e=>e.id))}>✓ Run Payroll — Pay All</button>
            </div>
          </>
        )}
      </>}

      {editEmpModal && (
        <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
          <MHdr title="Edit Employee" onClose={()=>setEditEmpModal(false)}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div style={{gridColumn:"1/-1"}}><Field label="Name *"><input style={st.input} value={editEmpData.name||""} onChange={e=>setEED("name",e.target.value)} autoFocus/></Field></div>
            <Field label="Role">
              <select style={st.input} value={editEmpData.role||"crew"} onChange={e=>setEED("role",e.target.value)}>
                <option value="crew">Foreman</option>
                <option value="apprentice">Apprentice</option>
              </select>
            </Field>
            <Field label="Pay Type">
              <select style={st.input} value={editEmpData.payType||"Hourly"} onChange={e=>setEED("payType",e.target.value)}>
                {PAY_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label={editEmpData.payType==="Hourly"||!editEmpData.payType?"Hourly Rate ($)":"Amount ($)"}>
              <input style={st.input} type="number" min="0" step="0.01" value={editEmpData.rate||""} onChange={e=>setEED("rate",e.target.value)} placeholder="0.00"/>
            </Field>
            <Field label="Phone">
              <input style={st.input} type="tel" value={editEmpData.phone||""} onChange={e=>setEED("phone",e.target.value)} placeholder="555-555-5555"/>
            </Field>
            <Field label="Email">
              <input style={st.input} type="email" value={editEmpData.email||""} onChange={e=>setEED("email",e.target.value)} placeholder="john@example.com"/>
            </Field>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
            <button style={st.btn()} onClick={()=>setEditEmpModal(false)}>Cancel</button>
            <button style={st.btn("primary")} onClick={saveEditEmp} disabled={!editEmpData.name?.trim()}>Save Changes</button>
          </div>
        </div></Overlay>
      )}

      {empModal && (
        <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
          <MHdr title="Add Employee" onClose={()=>setEmpModal(false)}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div style={{gridColumn:"1/-1"}}><Field label="Name *"><input style={st.input} value={empData.name||""} onChange={e=>setEMP("name",e.target.value)} placeholder="John Smith" autoFocus/></Field></div>
            <Field label="Role">
              <select style={st.input} value={empData.role||"crew"} onChange={e=>setEMP("role",e.target.value)}>
                <option value="crew">Foreman</option>
                <option value="apprentice">Apprentice</option>
              </select>
            </Field>
            <Field label="Pay Type">
              <select style={st.input} value={empData.payType||"Hourly"} onChange={e=>setEMP("payType",e.target.value)}>
                {PAY_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label={empData.payType==="Hourly"||!empData.payType?"Hourly Rate ($)":"Amount ($)"}>
              <input style={st.input} type="number" min="0" step="0.01" value={empData.rate||""} onChange={e=>setEMP("rate",e.target.value)} placeholder="0.00"/>
            </Field>
            <Field label="Phone">
              <input style={st.input} type="tel" value={empData.phone||""} onChange={e=>setEMP("phone",e.target.value)} placeholder="555-555-5555"/>
            </Field>
            <Field label="Email">
              <input style={st.input} type="email" value={empData.email||""} onChange={e=>setEMP("email",e.target.value)} placeholder="john@example.com"/>
            </Field>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
            <button style={st.btn()} onClick={()=>setEmpModal(false)}>Cancel</button>
            <button style={st.btn("primary")} onClick={saveEmp} disabled={!empData.name?.trim()}>Add Employee</button>
          </div>
        </div></Overlay>
      )}

      {entryModal && (
        <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
          <MHdr title="Log Pay Entry" onClose={()=>setEntryModal(null)}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <Field label="Employee">
              <select style={st.input} value={entryData.userId||""} onChange={e=>{
                const emp = employees.find(x=>x.id===e.target.value);
                setEN("userId", e.target.value);
                if (emp) { setEN("rate", emp.rate); setEN("payType", emp.pay_type||"Hourly"); }
              }}>
                {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </Field>
            <Field label="Date"><input style={st.input} type="date" value={entryData.date||""} onChange={e=>setEN("date",e.target.value)}/></Field>
            <Field label="Pay Type">
              <select style={st.input} value={entryData.payType||"Hourly"} onChange={e=>setEN("payType",e.target.value)}>
                {PAY_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </Field>
            {(entryData.payType==="Hourly"||entryData.payType==="Overtime") &&
              <Field label="Hours"><input style={st.input} type="number" min="0" step="0.25" value={entryData.hours||""} onChange={e=>setEN("hours",e.target.value)} placeholder="8"/></Field>
            }
            <Field label={entryData.payType==="Hourly"||entryData.payType==="Overtime"?"Hourly Rate ($)":"Amount ($)"}>
              <input style={st.input} type="number" min="0" step="0.01" value={entryData.rate||""} onChange={e=>setEN("rate",e.target.value)} placeholder="0.00"/>
            </Field>
            <Field label="Link to Job (optional)">
              <select style={st.input} value={entryData.jobId||""} onChange={e=>setEN("jobId",e.target.value?parseInt(e.target.value):"")}>
                <option value="">— None —</option>
                {toArr(jobs).map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={st.input} value={entryData.status||"Pending"} onChange={e=>setEN("status",e.target.value)}>
                {PAY_STATUS.map(s=><option key={s}>{s}</option>)}
              </select>
            </Field>
            <div style={{gridColumn:"1/-1"}}><Field label="Description / Notes"><input style={st.input} value={entryData.notes||""} onChange={e=>setEN("notes",e.target.value)} placeholder="e.g. Panel upgrade job, week of May 27"/></Field></div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
            <button style={st.btn()} onClick={()=>setEntryModal(null)}>Cancel</button>
            <button style={st.btn("primary")} onClick={saveEntry}>Save Entry</button>
          </div>
        </div></Overlay>
      )}
    </div>
  );
}
